import { useTranslation } from "react-i18next";
import { Flame, Gamepad2, Grid2X2, UsersRound } from "lucide-react";

import { AnimalFigure } from "@/components/camp/AnimalFigure";
import type { AnimalId } from "@/components/camp/campData";
import type { RoomMode } from "@/engine/state/roomControl";

import { LandingSection } from "./LandingSection";
import { LANDING_MODES } from "./modeList";

/**
 * The four kinds of room, said on the page rather than behind a sign-up.
 *
 * A visitor deciding whether this is for them should not have to open a room
 * to find out what a room can be. The words are the ones the room itself uses
 * — the same `roomMode.*Description` the settings panel shows — so the landing
 * cannot describe a mode differently from the mode (#615).
 *
 * Each card is drawn in its mode's own colour, over a sketch of what the room
 * looks like — a fire, bubbles in a glade, two teams and a hall, a grid of
 * tiles — with the icon the brandbook's navigation gives the mode and the
 * animals standing in it (#942).
 */

interface ModeLook {
  icon: typeof Flame;
  /** Sets `--mode` to a token of the palette, so the card follows the visitor's colours. */
  colour: string;
  scene: string;
  animals: readonly { animal: AnimalId; left: string }[];
}

// Built when drawn, not at import: the icons come from a chunk that imports this
// one back, and read at import time they were `undefined` on the production
// landing.
function looks(): Record<RoomMode, ModeLook> {
  return {
    campfire: {
      icon: Flame,
      colour: "[--mode:var(--primary)]",
      scene:
        "bg-[radial-gradient(ellipse_45%_60%_at_50%_100%,hsl(var(--mode)/0.55),transparent_70%),linear-gradient(to_bottom,hsl(var(--background)),hsl(var(--card)))]",
      animals: [
        { animal: "fox", left: "30%" },
        { animal: "hare", left: "70%" },
      ],
    },
    bubbles: {
      icon: UsersRound,
      colour: "[--mode:var(--identity-moss)]",
      scene:
        "bg-[radial-gradient(circle_at_30%_70%,hsl(var(--mode)/0.35)_0_22%,transparent_23%),radial-gradient(circle_at_76%_58%,hsl(var(--mode)/0.25)_0_17%,transparent_18%),linear-gradient(to_bottom,hsl(var(--mode)/0.12),hsl(var(--card)))]",
      animals: [
        { animal: "frog", left: "24%" },
        { animal: "deer", left: "37%" },
        { animal: "mouse", left: "76%" },
      ],
    },
    teams: {
      icon: Gamepad2,
      colour: "[--mode:var(--identity-sky)]",
      scene:
        "bg-[linear-gradient(to_right,hsl(var(--identity-rust)/0.3)_0_34%,hsl(var(--card))_34%_66%,hsl(var(--mode)/0.3)_66%)]",
      animals: [
        { animal: "bear", left: "17%" },
        { animal: "owl", left: "50%" },
        { animal: "wolf", left: "83%" },
      ],
    },
    plain: {
      icon: Grid2X2,
      colour: "[--mode:var(--identity-lilac)]",
      scene:
        "bg-[linear-gradient(hsl(var(--card))_0_0)] [background-image:linear-gradient(hsl(var(--mode)/0.22),hsl(var(--mode)/0.22)),linear-gradient(hsl(var(--mode)/0.12),hsl(var(--mode)/0.12)),linear-gradient(hsl(var(--mode)/0.12),hsl(var(--mode)/0.12)),linear-gradient(hsl(var(--mode)/0.22),hsl(var(--mode)/0.22))] [background-position:8%_14%,92%_14%,8%_86%,92%_86%] [background-size:42%_36%] bg-no-repeat",
      animals: [
        { animal: "cat", left: "29%" },
        { animal: "raccoon", left: "71%" },
      ],
    },
  };
}

function ModeScene({ look }: { look: ModeLook }) {
  const Icon = look.icon;
  return (
    <div
      aria-hidden="true"
      className={`relative h-36 overflow-hidden rounded-2xl border border-[hsl(var(--mode)/0.35)] ${look.scene}`}
    >
      <span className="absolute top-3 left-3 grid size-10 place-items-center rounded-xl bg-[hsl(var(--mode)/0.22)] text-[hsl(var(--mode))] shadow-glow-sm">
        <Icon className="size-5" />
      </span>
      {look.animals.map(({ animal, left }) => (
        <AnimalFigure
          key={animal}
          animal={animal}
          className="absolute bottom-2 !h-20 !w-14 -translate-x-1/2"
          style={{ left }}
        />
      ))}
    </div>
  );
}

export function LandingModes() {
  const { t } = useTranslation();

  return (
    <LandingSection
      id="modes"
      eyebrow={t("landing.modes.eyebrow")}
      title={t("landing.modes.title")}
      lead={t("landing.modes.lead")}
    >
      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {LANDING_MODES.map((mode) => {
          const look = looks()[mode];
          return (
            <li
              key={mode}
              data-testid={`landing-mode-${mode}`}
              className={`${look.colour} flex flex-col gap-4 rounded-3xl border border-[hsl(var(--mode)/0.35)] bg-[linear-gradient(to_bottom,hsl(var(--mode)/0.1),hsl(var(--card))_45%)] p-4 text-left`}
            >
              <ModeScene look={look} />
              <div className="px-1 pb-1">
                <h3 className="mb-2 text-lg font-semibold text-[hsl(var(--mode))]">
                  {t(`roomMode.${mode}`)}
                </h3>
                <p className="text-pretty text-sm text-muted-foreground">
                  {t(`roomMode.${mode}Description`)}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </LandingSection>
  );
}
